import { useEffect, useState } from "react"
import { useDispatch } from "react-redux"
import { useSearchParams } from "react-router-dom"
import { Card, CardBody, Container, Table } from "reactstrap"
import withRouter from "Components/Common/withRouter"
import Breadcrumb from "Components/organisms/breadcrumb"
import ActionButton from "Components/atoms/buttons/action-button"
import Chip from "Components/atoms/chip"
import MarkdownViewer from "Components/molecule/markdown-viewer"
import AreYouSureModal from "Components/organisms/are-you-sure-modal"
import LoadingState from "Components/organisms/loading-state"
import { GITHUB_PR_URL, MemoDetailResult } from "models/memo"
import { invalidateMemos } from "slices/admin/memos/reducer"
import { getMemo, sendMemo } from "slices/admin/memos/thunk"
import { navigateToMemoList } from "utils/navigator"
import { formatDateTimeAmPm } from "utils/tools"

// Route: /admin/system-updates-memo/details?slug=<file name without .md>
const MemoDetailsPage = (props: any) => {
    const dispatch: any = useDispatch()
    const [searchParams] = useSearchParams()
    const slug = searchParams.get("slug") ?? ""

    const [data, setData] = useState<MemoDetailResult | null>(null)
    const [isLoading, setIsLoading] = useState<boolean>(true)
    const [isSendingTest, setIsSendingTest] = useState<boolean>(false)
    const [confirmSend, setConfirmSend] = useState<boolean>(false)

    const note = data?.note
    document.title = `${note?.title ?? "Memo"} | IbexIQ`

    const load = async () => {
        setData(await getMemo(slug))
    }

    useEffect(() => {
        const init = async () => {
            if (slug) await load()
            setIsLoading(false)
        }
        init()
    }, [slug])

    const handleBack = () => navigateToMemoList(props.router.navigate)

    const handleSendTest = async () => {
        setIsSendingTest(true)
        const res = await sendMemo(slug, { test: true })
        if (res) await load()
        setIsSendingTest(false)
    }

    // A note already sent to everyone needs force (the confirmation says so)
    const handleSendAll = async () => {
        const res = await sendMemo(slug, { force: !!note?.publishedAt })
        if (res) {
            dispatch(invalidateMemos())
            await load()
            setConfirmSend(false)
        }
    }

    const closeConfirm = async () => setConfirmSend(false)

    const confirmText = note?.publishedAt
        ? `This memo was already sent to everyone on ${formatDateTimeAmPm(note.publishedAt)}. Send it again to ${data?.recipientCount ?? 0} active employees`
        : `"${note?.title ?? ""}" will be emailed to ${data?.recipientCount ?? 0} active employees and will appear in the Version Log. Are you sure`

    return (
        <div className="page-content">
            <Container fluid style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
                <Breadcrumb
                    title="System Updates Memo"
                    breadcrumbItem={note?.title ?? "Memo"}
                    actions={
                        <>
                            <ActionButton onClick={handleBack}>Back</ActionButton>
                            {note && <>
                                <ActionButton onClick={handleSendTest} disabled={isSendingTest}>
                                    {isSendingTest ? "Sending..." : "Send test to me"}
                                </ActionButton>
                                <ActionButton onClick={() => setConfirmSend(true)}>
                                    {note.publishedAt ? "Send again to everyone" : "Send to everyone"}
                                </ActionButton>
                            </>}
                        </>
                    }
                />

                {isLoading && <LoadingState title="Loading memo" />}

                {!isLoading && !note && (
                    <p className="text-muted">This memo is not available.</p>
                )}

                {!isLoading && note && <>
                    <Card>
                        <CardBody className="d-flex flex-column gap-2">
                            <div className="d-flex flex-wrap align-items-center gap-3">
                                {note.publishedAt
                                    ? <Chip variant="success">Sent</Chip>
                                    : <Chip variant="secondary">Draft</Chip>}
                                {note.periodEnd && (
                                    <span className="text-muted">
                                        <i className="bx bx-calendar me-1"></i>
                                        {note.periodStart ? `${note.periodStart} → ${note.periodEnd}` : `Up to ${note.periodEnd}`}
                                    </span>
                                )}
                                {note.lastCommit && <span className="text-muted"><i className="bx bx-git-commit me-1"></i>{note.lastCommit}</span>}
                            </div>
                            {note.prs.length > 0 && (
                                <div className="d-flex flex-wrap gap-2">
                                    <span className="text-muted">Pull requests:</span>
                                    {note.prs.map(pr => (
                                        <a key={pr} href={`${GITHUB_PR_URL}/${pr}`} target="_blank" rel="noreferrer">#{pr}</a>
                                    ))}
                                </div>
                            )}
                            {note.screens.length > 0 && (
                                <div className="d-flex flex-wrap gap-2">
                                    <span className="text-muted">Screens:</span>
                                    {note.screens.map(s => <Chip key={s.name} variant="primary">{s.name}</Chip>)}
                                </div>
                            )}
                        </CardBody>
                    </Card>

                    <Card>
                        <CardBody>
                            <h4 className="mb-1">{note.title}</h4>
                            {note.summary && <p className="text-muted">{note.summary}</p>}
                            <MarkdownViewer content={note.content} />
                        </CardBody>
                    </Card>

                    <Card>
                        <CardBody>
                            <h5 className="mb-3">Send history</h5>
                            {note.sends.length === 0
                                ? <p className="text-muted mb-0">Not sent yet.</p>
                                : <Table size="sm" className="mb-0">
                                    <thead>
                                        <tr><th>Sent</th><th>Type</th><th>Recipients</th><th>By</th></tr>
                                    </thead>
                                    <tbody>
                                        {note.sends.map(s => (
                                            <tr key={s.id}>
                                                <td>{formatDateTimeAmPm(s.sentAt)}</td>
                                                <td>{s.testOnly ? <Chip variant="secondary">Test</Chip> : <Chip variant="success">Everyone</Chip>}</td>
                                                <td>{s.recipientCount}</td>
                                                <td>{s.sentByEmail}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>}
                        </CardBody>
                    </Card>
                </>}
            </Container>

            <AreYouSureModal
                isOpen={confirmSend}
                onModalClose={closeConfirm}
                modalTitle="Send memo"
                subTitle={confirmText}
                handleAreYouSureAccept={handleSendAll}
                handleAreYouSureDecline={closeConfirm}
                areYouSureacceptText="Send"
                areYouSuredeclineText="Cancel"
            />
        </div>
    )
}

export default withRouter(MemoDetailsPage)
